import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../api/apiClient";
import type {
  BouquetTemplate,
  CatalogItem,
  CreateShopifyLinkPayload,
  ShopifyLinkResult,
  ShopifyVariant,
} from "../../api/types";
import { BouquetTemplateThumb } from "../BouquetTemplateThumb";
import { ItemThumb } from "../ItemThumb";
import { Modal } from "../Modal";
import { Chip } from "../ui/Chip";
import { SegmentedControl } from "../ui/SegmentedControl";
import { shopifyVariantLabel } from "./shopifyVariantLabel";

// Lazy so the scanner's decoder is only downloaded when someone scans.
const BarcodeScanner = lazy(() =>
  import("../BarcodeScanner").then((m) => ({ default: m.BarcodeScanner })),
);

type Tab = "recipes" | "items";

/** Rows past this are hidden behind "refine the search" — a list, not a catalogue dump. */
const ITEM_ROW_LIMIT = 60;

function errText(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/**
 * G-111 S4: choose what one Shopify size stands for — a bouquet recipe or a
 * stock item — with search, category chips, photos and a barcode scan.
 */
export function ShopifyLinkPicker({
  variant,
  onClose,
  onLinked,
}: {
  variant: ShopifyVariant;
  onClose: () => void;
  onLinked: (result: ShopifyLinkResult) => void;
}) {
  const [tab, setTab] = useState<Tab>(
    variant.linkedTo?.type === "ITEM" ? "items" : "recipes",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const link = async (payload: Omit<CreateShopifyLinkPayload, "shopifyVariantId">) => {
    setSaving(true);
    setError(null);
    try {
      onLinked(
        await api.setShopifyLink({ shopifyVariantId: variant.shopifyVariantId, ...payload }),
      );
    } catch (e) {
      setError(errText(e, "Could not link"));
      setSaving(false);
    }
  };

  return (
    <Modal title={`Link "${shopifyVariantLabel(variant)}"`} onClose={onClose}>
      <div className="shopify-picker">
        {variant.linkedTo ? (
          <p className="shopify-picker__current">
            Currently linked to {variant.linkedTo.name}. Picking another replaces it.
          </p>
        ) : null}
        <SegmentedControl
          ariaLabel="Link to"
          options={[
            { value: "recipes", label: "Bouquet recipes" },
            { value: "items", label: "Items" },
          ]}
          value={tab}
          onChange={setTab}
          disabled={saving}
        />
        {error ? <div className="alert error">{error}</div> : null}
        {tab === "items" ? (
          <ItemsTab
            variant={variant}
            disabled={saving}
            onPick={(item) => void link({ linkType: "ITEM", itemId: item.id })}
          />
        ) : (
          <RecipesTab
            variant={variant}
            disabled={saving}
            onPick={(template) =>
              void link({ linkType: "BOUQUET_TEMPLATE", bouquetTemplateId: template.id })
            }
          />
        )}
      </div>
    </Modal>
  );
}

function ItemsTab({
  variant,
  disabled,
  onPick,
}: {
  variant: ShopifyVariant;
  disabled: boolean;
  onPick: (item: CatalogItem) => void;
}) {
  const [items, setItems] = useState<CatalogItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scannedCode, setScannedCode] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listItems({ includeInactive: false })
      // Fresh flowers link only through recipes (G-60); the server refuses them too.
      .then((rows) => !cancelled && setItems(rows.filter((item) => !item.isPerishable)))
      .catch((e) => !cancelled && setError(errText(e, "Could not load items")));
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of items ?? []) seen.set(item.category.id, item.category.label);
    return [...seen].sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);

  const suggested = useMemo(() => {
    const barcode = variant.barcode?.trim();
    const sku = variant.sku?.trim().toLowerCase();
    if (!barcode && !sku) return [];
    return (items ?? []).filter(
      (item) =>
        (barcode && item.barcode?.trim() === barcode) ||
        (sku && item.sku?.trim().toLowerCase() === sku),
    );
  }, [items, variant.barcode, variant.sku]);

  const matches = useMemo(() => {
    if (!items) return [];
    if (scannedCode) return items.filter((item) => item.barcode?.trim() === scannedCode);
    const q = query.trim().toLowerCase();
    return items.filter(
      (item) =>
        (!categoryId || item.category.id === categoryId) &&
        (!q ||
          item.name.toLowerCase().includes(q) ||
          item.sku?.toLowerCase().includes(q) ||
          item.barcode?.includes(q)),
    );
  }, [items, query, categoryId, scannedCode]);

  if (error) return <div className="alert error">{error}</div>;
  if (!items) return <p className="muted">Loading items...</p>;

  const suggestedIds = new Set(suggested.map((item) => item.id));
  const rest = matches.filter((item) => !suggestedIds.has(item.id));
  const shown = rest.slice(0, ITEM_ROW_LIMIT);

  return (
    <>
      <div className="shopify-picker__search">
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setScannedCode(null);
          }}
          placeholder="Search by name, SKU or barcode"
          aria-label="Search items"
        />
        <button type="button" className="button tinted" onClick={() => setScanning(true)}>
          Scan
        </button>
      </div>
      {categories.length > 1 && !scannedCode ? (
        <div className="shopify-picker__chips">
          <Chip label="All" selected={categoryId === null} onClick={() => setCategoryId(null)} />
          {categories.map(([id, label]) => (
            <Chip key={id} label={label} selected={categoryId === id} onClick={() => setCategoryId(id)} />
          ))}
        </div>
      ) : null}
      {scannedCode ? (
        <p className="shopify-picker__note">
          Barcode {scannedCode}:{" "}
          {matches.length === 0
            ? "no item has it."
            : matches.length === 1
              ? "one item."
              : `${matches.length} items share it — tap the one this product is.`}{" "}
          <button type="button" className="button plain" onClick={() => setScannedCode(null)}>
            Clear
          </button>
        </p>
      ) : null}
      <div className="shopify-picker__list">
        {!scannedCode
          ? suggested.map((item) => (
              <ItemRow key={item.id} item={item} suggested disabled={disabled} onPick={onPick} />
            ))
          : null}
        {(scannedCode ? matches : shown).map((item) => (
          <ItemRow key={item.id} item={item} disabled={disabled} onPick={onPick} />
        ))}
        {!scannedCode && rest.length > ITEM_ROW_LIMIT ? (
          <p className="shopify-picker__note">
            Showing {ITEM_ROW_LIMIT} of {rest.length}. Refine the search to see the rest.
          </p>
        ) : null}
        {matches.length === 0 && suggested.length === 0 && !scannedCode ? (
          <p className="shopify-picker__note">No item matches.</p>
        ) : null}
      </div>
      {scanning ? (
        <Suspense fallback={null}>
          <BarcodeScanner
            onDetected={(code) => {
              setScanning(false);
              setScannedCode(code.trim());
            }}
            onClose={() => setScanning(false)}
          />
        </Suspense>
      ) : null}
    </>
  );
}

function ItemRow({
  item,
  suggested = false,
  disabled,
  onPick,
}: {
  item: CatalogItem;
  suggested?: boolean;
  disabled: boolean;
  onPick: (item: CatalogItem) => void;
}) {
  return (
    <button
      type="button"
      className={suggested ? "shopify-picker__row is-suggested" : "shopify-picker__row"}
      disabled={disabled}
      onClick={() => onPick(item)}
    >
      <ItemThumb itemId={item.id} imageKey={item.imageKey ?? null} alt="" size={44} />
      <span className="shopify-picker__row-text">
        <span>{item.name}</span>
        <small>
          {suggested ? "Same barcode or SKU as this product · " : ""}
          {item.category.label}
          {item.barcode ? ` · ${item.barcode}` : ""}
        </small>
      </span>
    </button>
  );
}

function RecipesTab({
  variant,
  disabled,
  onPick,
}: {
  variant: ShopifyVariant;
  disabled: boolean;
  onPick: (template: BouquetTemplate) => void;
}) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<BouquetTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .listBouquetTemplates()
      .then((rows) => !cancelled && setTemplates(rows.filter((row) => row.isActive)))
      .catch((e) => !cancelled && setError(errText(e, "Could not load recipes")));
    return () => {
      cancelled = true;
    };
  }, []);

  const createFromProduct = () => {
    const params = new URLSearchParams({
      shopifyVariant: variant.shopifyVariantId,
      returnTo: "/app/shopify/products",
    });
    navigate(`/app/bouquets/templates/new?${params.toString()}`);
  };

  if (error) return <div className="alert error">{error}</div>;
  if (!templates) return <p className="muted">Loading recipes...</p>;

  const q = query.trim().toLowerCase();
  const matches = templates.filter((t) => !q || t.name.toLowerCase().includes(q));

  return (
    <>
      <div className="shopify-picker__search">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search recipes"
          aria-label="Search recipes"
        />
        <button type="button" className="button tinted" onClick={createFromProduct} disabled={disabled}>
          Create recipe from this product
        </button>
      </div>
      {matches.length === 0 ? (
        <p className="shopify-picker__note">
          {templates.length === 0 ? "No recipes yet." : "No recipe matches."} Create one from this
          product instead.
        </p>
      ) : (
        <div className="shopify-picker__grid">
          {matches.map((template) => (
            <button
              key={template.id}
              type="button"
              className="shopify-picker__recipe"
              disabled={disabled}
              onClick={() => onPick(template)}
            >
              <BouquetTemplateThumb
                templateId={template.id}
                imageKey={template.imageKey}
                alt=""
                size={96}
              />
              <span>{template.name}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}
